import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Activity, BookOpen, Wrench, Check } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { RadarChartCard } from "@/components/charts/RadarChartCard";
import { subscribeNewsletter } from "@/lib/api";
import { Seo } from "@/components/Seo";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_planning-hub-31/artifacts/cdgvvd5g_timelab-sWOvgOOFk1g-unsplash.jpg";

const FEATURES = [
  {
    icon: Activity,
    title: "Intelligent SCM Scan",
    desc: "AI-powered maturity assessment across Demand Planning, Inventory Planning, Supply Planning, and S&OP. Free to start. Results in 15 minutes.",
    link: "Take the assessment",
    to: "/scan",
  },
  {
    icon: BookOpen,
    title: "Insights & Frameworks",
    desc: "Analyst-grade supply chain content across Plan, Source, Make, and Deliver. No fluff. Built for practitioners.",
    link: "Read latest insights",
    to: "/insights",
  },
  {
    icon: Wrench,
    title: "SCM Tools",
    desc: "Practical calculators, scorecards, and templates for supply chain teams. Free to use.",
    link: "Explore tools",
    to: "/tools",
  },
];

const CREDIBILITY = ["APICS", "Gartner", "McKinsey", "Deloitte", "Oliver Wight"];

const INSIGHTS = [
  { tag: "Demand Planning", title: "Why FVA beats MAPE for measuring planner value", desc: "Forecast Value Add reveals which steps actually improve your baseline." },
  { tag: "S&OP", title: "The executive S&OP meeting that actually drives decisions", desc: "Most S&OP cycles produce slides, not decisions. Here's how to flip that." },
  { tag: "AI in SCM", title: "Where GenAI genuinely helps demand planners today", desc: "A practitioner's view on copilots, exception narratives, and demand sensing." },
];

const RADAR_SAMPLE = [
  { axis: "Data Prep", oms: 5.4 },
  { axis: "Stat Forecast", oms: 4.1 },
  { axis: "Commercial", oms: 6.2 },
  { axis: "DPR", oms: 3.5 },
  { axis: "Publication", oms: 5.0 },
];

export default function Home() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.message);
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Seo
        title="SCMBrain | AI-Powered Supply Chain Planning Maturity Assessment"
        description="Free AI-powered supply chain planning maturity assessment. Evaluate Demand Planning, Inventory Planning, Supply Planning, and S&OP capabilities in minutes."
        path="/"
      />
      {/* HERO */}
      <section className="relative h-[500px] overflow-hidden flex items-center">
        <img
          src={HERO_IMG} alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-[#1B3A6B]/65" />
        <div className="relative w-full max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="text-sm uppercase tracking-[0.2em] text-[#5FC9B5] font-semibold mb-6"
          >
            Supply Chain Intelligence
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.07]"
          >
            Supply chain intelligence for practitioners who want to lead, not react.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mt-6 text-lg text-[#D6E0EF] max-w-2xl mx-auto leading-relaxed"
          >
            Videos, frameworks, assessments, and tools — built by practitioners, for practitioners
            across Plan, Source, Make, and Deliver.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              data-testid="hero-start-assessment-btn"
              onClick={() => navigate("/scan")}
              className="group bg-[#1A6B5A] text-white hover:bg-[#135245] transition-colors rounded-sm px-7 py-3.5 font-semibold flex items-center gap-2"
            >
              Start Free Assessment
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <Link
              data-testid="hero-explore-btn"
              to="/insights"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1B3A6B] transition-colors rounded-sm px-7 py-3.5 font-semibold"
            >
              Explore SCMBrain
            </Link>
          </motion.div>
          <p className="mt-6 text-xs text-[#B8C4D6] max-w-lg mx-auto">
            Assessment results are for informational purposes only and do not constitute professional
            consulting advice.
          </p>
        </div>
      </section>

      {/* FEATURE CARDS */}
      <section className="bg-white max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="group bg-[#F4F4F4] border border-[#E5E7EB] border-l-4 border-l-[#1A6B5A] rounded-sm p-8 hover:-translate-y-1 transition-transform"
              data-testid={`feature-card-${i}`}
            >
              <f.icon className="h-8 w-8 text-[#1A6B5A] mb-5" strokeWidth={1.8} />
              <h3 className="font-heading text-xl font-bold text-[#1B3A6B] mb-3">{f.title}</h3>
              <p className="text-[#3E577A] text-sm leading-relaxed mb-6">{f.desc}</p>
              <Link to={f.to} className="text-[#1A6B5A] font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
                {f.link} <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CREDIBILITY STRIP */}
      <section className="bg-[#F4F4F4] border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-center gap-6 md:gap-12">
          <span className="text-xs uppercase tracking-[0.18em] text-[#3E577A] font-semibold whitespace-nowrap">
            Frameworks referenced from:
          </span>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {CREDIBILITY.map((c) => (
              <span key={c} className="font-heading font-bold text-[#1B3A6B]/70 text-lg tracking-tight">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TOOL — DARK NAVY */}
      <section className="bg-[#1B3A6B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white leading-tight">
              Know exactly where your supply chain planning stands.
            </h2>
            <p className="mt-5 text-[#C5D2E6] leading-relaxed">
              The Intelligent SCM Scan gives you a maturity score across all four planning functions
              — free, in 15 minutes, with an AI-generated diagnosis.
            </p>
            <button
              data-testid="featured-start-assessment-btn"
              onClick={() => navigate("/scan")}
              className="mt-8 bg-[#1A6B5A] text-white hover:bg-[#135245] transition-colors rounded-sm px-7 py-3.5 font-semibold inline-flex items-center gap-2"
            >
              Start Free Assessment <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="bg-white rounded-md p-6 shadow-2xl">
            <p className="text-xs uppercase tracking-[0.18em] text-[#1A6B5A] font-semibold mb-2">Maturity Radar</p>
            <RadarChartCard data={RADAR_SAMPLE} height={320} />
          </div>
        </div>
      </section>

      {/* LATEST INSIGHTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A6B]">Latest insights</h2>
          <Link to="/insights" className="text-[#1A6B5A] font-semibold text-sm hidden sm:inline-flex items-center gap-1 hover:gap-2 transition-all">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {INSIGHTS.map((it, i) => (
            <Link
              to="/insights" key={i} data-testid={`insight-card-${i}`}
              className="group bg-white border border-[#E5E7EB] rounded-sm p-7 hover:-translate-y-1 hover:shadow-lg transition-all"
            >
              <span className="text-xs font-semibold uppercase tracking-wider text-[#1A6B5A]">{it.tag}</span>
              <h3 className="font-heading text-lg font-bold text-[#1B3A6B] mt-3 mb-2 leading-snug">{it.title}</h3>
              <p className="text-sm text-[#3E577A] leading-relaxed mb-4">{it.desc}</p>
              <span className="text-[#1A6B5A] font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Read more <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* NEWSLETTER — LIGHT TEAL */}
      <section className="bg-[#E6F2EF]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A6B]">
            Supply chain intelligence, every week.
          </h2>
          <p className="mt-4 text-[#3E577A]">
            One email. Frameworks, tools, and analysis for practitioners who want to stay ahead.
          </p>
          <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              data-testid="newsletter-email-input"
              aria-label="Email address"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="flex-1 rounded-sm border border-[#1A6B5A]/30 bg-white px-4 py-3 text-sm text-[#1B3A6B] focus:outline-none focus:ring-2 focus:ring-[#1A6B5A]"
            />
            <button
              data-testid="newsletter-subscribe-btn" disabled={loading}
              className="bg-[#1A6B5A] text-white hover:bg-[#135245] transition-colors rounded-sm px-6 py-3 font-semibold disabled:opacity-60"
            >
              {loading ? "Subscribing…" : "Subscribe Free"}
            </button>
          </form>
          <p className="mt-4 text-xs text-[#3E577A] flex items-center justify-center gap-1">
            <Check className="h-3.5 w-3.5 text-[#1A6B5A]" /> No spam. Unsubscribe anytime.
          </p>
        </div>
      </section>
    </Layout>
  );
}
